import React from 'react';
import { useTranslation } from 'localization';
import StarDisplayAtom from '../../atoms/marketplace/StarDisplayAtom';
import { RatingDistribution } from 'types/marketplace';

export interface RatingSummaryMoleculeProps {
  ratingAverage: number;
  ratingCount: number;
  distribution: RatingDistribution;
  className?: string;
}

const STAR_LEVELS = [5, 4, 3, 2, 1] as const;

const RatingSummaryMolecule: React.FC<RatingSummaryMoleculeProps> = ({
  ratingAverage: rawRatingAverage,
  ratingCount,
  distribution,
  className = '',
}) => {
  const { t } = useTranslation();
  const ratingAverage = Number(rawRatingAverage) || 0;

  const classes = ['marketplace-rating-summary', className]
    .filter(Boolean)
    .join(' ');

  const maxBucketCount = Math.max(
    ...STAR_LEVELS.map(
      (lvl) => distribution[String(lvl) as keyof RatingDistribution] ?? 0,
    ),
    1,
  );

  // Render the histogram even when there are 0 ratings so the layout
  // matches the design (Marketplace V3 - Publishers.html `RatingsBreakdown`).
  // The empty-state copy lives next to the score block instead of replacing
  // the histogram.
  const isEmpty = ratingCount === 0;

  return (
    <div className={classes}>
      <div className="marketplace-rating-summary__overview">
        <div className="marketplace-rating-summary__score">
          <span className="marketplace-rating-summary__average">
            {isEmpty ? '—' : ratingAverage.toFixed(1)}
          </span>
          <StarDisplayAtom rating={ratingAverage} size="normal" />
          <span className="marketplace-rating-summary__count">
            {isEmpty
              ? t('marketplace.ratings.noRatings')
              : t('marketplace.ratings.ratingCount', { count: ratingCount })}
          </span>
        </div>
      </div>
      <div className="marketplace-rating-summary__distribution">
        {STAR_LEVELS.map((level) => {
          const count = distribution[String(level) as keyof RatingDistribution] ?? 0;
          const percentage = isEmpty ? 0 : (count / maxBucketCount) * 100;

          return (
            <div key={level} className="marketplace-rating-summary__bar-row">
              <span className="marketplace-rating-summary__bar-label">
                {t(`marketplace.ratings.distribution.star${level}`)}
              </span>
              <div className="marketplace-rating-summary__bar-track">
                <div
                  className="marketplace-rating-summary__bar-fill"
                  style={{ width: `${percentage}%` }}
                  role="meter"
                  aria-valuenow={percentage}
                  aria-valuemin={0}
                  aria-valuemax={100}
                />
              </div>
              <span className="marketplace-rating-summary__bar-count">
                {count}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RatingSummaryMolecule;
